/**
 * Seeded RNG family tree.
 *
 * A root seed string derives independent child streams by LABEL, never by
 * sequential draws: child state = cyrb128(root + "\x1f" + labelPath). Adding a
 * new labeled system later cannot perturb any existing stream — that property
 * is load-bearing for stable seeds across app versions and is enforced by
 * tests/rng_test.ts.
 *
 * PRNG: sfc32 (128-bit state, passes PractRand, ~4 int ops/draw, pure 32-bit
 * JS so results are bit-identical across engines).
 */

const SEP = "\x1f";

/** Hash a string to four 32-bit words (cyrb128, public domain by bryc). */
export function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0, k: number; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [
    (h1 ^ h2 ^ h3 ^ h4) >>> 0,
    (h2 ^ h1) >>> 0,
    (h3 ^ h1) >>> 0,
    (h4 ^ h1) >>> 0,
  ];
}

export interface Rng {
  /** Uniform in [0, 1). Exact IEEE-754 op — identical across engines. */
  float(): number;
  /** Uniform integer in [minIncl, maxExcl). */
  int(minIncl: number, maxExcl: number): number;
  chance(p: number): boolean;
  pick<T>(items: readonly T[]): T;
  /** Weighted pick; entries with weight <= 0 are never chosen. */
  weighted<T>(entries: readonly (readonly [T, number])[]): T;
  /** Fisher–Yates; returns a copy, input untouched. */
  shuffle<T>(items: readonly T[]): T[];
  /** Derived stream for `${this.path}/${label}`; does NOT advance this one. */
  child(label: string): Rng;
  /** Full label path, e.g. "wfc/retry/1" — for debugging. */
  readonly path: string;
}

class Sfc32Rng implements Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(
    private readonly rootSeed: string,
    readonly path: string,
  ) {
    const [a, b, c, d] = cyrb128(rootSeed + SEP + path);
    this.a = a;
    this.b = b;
    this.c = c;
    this.d = d;
    // Warm-up: decorrelates streams whose seed material differs slightly.
    for (let i = 0; i < 12; i++) this.next32();
  }

  private next32(): number {
    const t = (this.a + this.b) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = ((this.c << 21) | (this.c >>> 11)) | 0;
    this.d = (this.d + 1) | 0;
    const r = (t + this.d) | 0;
    this.c = (this.c + r) | 0;
    return r >>> 0;
  }

  float(): number {
    return this.next32() / 4294967296;
  }

  int(minIncl: number, maxExcl: number): number {
    return minIncl + Math.floor(this.float() * (maxExcl - minIncl));
  }

  chance(p: number): boolean {
    return this.float() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) {
      throw new Error(`rng.pick on empty array (${this.path})`);
    }
    return items[this.int(0, items.length)];
  }

  weighted<T>(entries: readonly (readonly [T, number])[]): T {
    let total = 0;
    for (const [, w] of entries) if (w > 0) total += w;
    if (total <= 0) {
      throw new Error(`rng.weighted: no positive weights (${this.path})`);
    }
    let r = this.float() * total;
    for (const [item, w] of entries) {
      if (w <= 0) continue;
      r -= w;
      if (r < 0) return item;
    }
    return entries[entries.length - 1][0];
  }

  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i + 1);
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  child(label: string): Rng {
    return new Sfc32Rng(this.rootSeed, `${this.path}/${label}`);
  }
}

/** Entry point: one independent stream per (rootSeed, label). */
export function rngFamily(rootSeed: string, label: string): Rng {
  return new Sfc32Rng(rootSeed, label);
}
